/**
 * Energiebedarf: Grundumsatz × Alltag, Sport einzeln obendrauf.
 *
 * Alle Zahlen stehen mit Quelle in `docs/fachkonzept.md` (§ 2, Energiebedarf).
 * Es sind **Schätzungen** — die App sagt das an jeder Stelle, an der sie eine
 * davon zeigt. Sie protokolliert, sie verordnet nicht: Ein Zielvorschlag wird
 * nur übernommen, wenn man ihn antippt.
 *
 * Warum Alltag und Sport getrennt: Die üblichen Aktivitätsfaktoren enthalten
 * den Sport schon. Wer dann das Training noch einmal addiert, zählt es doppelt.
 * Deshalb beschreibt die Alltagsstufe den Tag **ohne** Sport, und jede Einheit
 * kommt mit ihrem Mehrverbrauch dazu.
 */

export const ENERGY_SOURCE =
  'Mifflin-St Jeor 1990; PAL nach DGE-Referenzwerten; MET nach Compendium of Physical Activities (Ainsworth 2011)';

export const SEX_FORMULAS = ['male', 'female'] as const;
export type SexFormula = (typeof SEX_FORMULAS)[number];

/** Alltag ohne Sport — vier Stufen, PAL nach DGE (Mitte der jeweiligen Spanne). */
export const ACTIVITY_LEVELS = ['sedentary', 'light', 'onFeet', 'heavy'] as const;
export type ActivityLevel = (typeof ACTIVITY_LEVELS)[number];

export const ACTIVITY_PAL: Record<ActivityLevel, number> = {
  /** Überwiegend sitzend, wenig Bewegung in der Freizeit (Büro). DGE 1,4–1,5. */
  sedentary: 1.45,
  /** Sitzend, zeitweise gehend oder stehend. DGE 1,6–1,7. */
  light: 1.65,
  /** Überwiegend gehend oder stehend (Verkauf, Handwerk, Pflege). DGE 1,8–1,9. */
  onFeet: 1.85,
  /** Körperlich anstrengende Arbeit (Bau, Landwirtschaft). DGE 2,0–2,4. */
  heavy: 2.2,
};

export const GOALS = ['lose', 'maintain', 'gain'] as const;
export type Goal = (typeof GOALS)[number];

/** Vorgaben fürs Tempo, in Prozent Körpergewicht je Woche (fachkonzept.md). */
export const DEFAULT_GOAL_RATE_PCT: Record<Goal, number> = {
  lose: 0.5,
  maintain: 0,
  gain: 0.25,
};

/** Grenzen fürs Tempo — darüber schlägt die App nichts mehr vor. */
export const MAX_GOAL_RATE_PCT = 1;

/** Energiegehalt von einem Kilogramm Körpergewichtsänderung (Faustwert, fachkonzept.md). */
export const KCAL_PER_KG_BODY = 7700;

/** Protein-Vorschlag je kg Körpergewicht — wie die Startwerte im Fachkonzept. */
export const PROTEIN_G_PER_KG = 1.9;

/** Fett je kg Körpergewicht — Untergrenze laut Fachkonzept; Kohlenhydrate sind der Rest. */
export const FAT_G_PER_KG = 0.8;

/**
 * Aktivitäten mit MET-Wert (Compendium of Physical Activities). `strength` ist
 * das Krafttraining der App selbst; die übrigen wählt man an einem Termin.
 */
export const ACTIVITIES = {
  strength: 3.5,
  walking: 3.5,
  hiking: 6.0,
  running: 9.8,
  cycling: 6.8,
  swimming: 5.8,
  yoga: 2.5,
  gardening: 3.8,
  football: 7.0,
  dancing: 5.0,
} as const;
export type Activity = keyof typeof ACTIVITIES;
export const ACTIVITY_KEYS = Object.keys(ACTIVITIES) as Activity[];

export function isActivity(value: unknown): value is Activity {
  return typeof value === 'string' && Object.hasOwn(ACTIVITIES, value);
}

export interface BodyFacts {
  birthYear: number | null;
  sex: SexFormula | null;
  heightCm: number | null;
  weightKg: number | null;
}

/** Was für eine Rechnung fehlt — ausdrücklich, statt still mit Ersatzwerten zu rechnen. */
export type MissingFact = 'birthYear' | 'sex' | 'heightCm' | 'weightKg';

export function missingFacts(facts: BodyFacts): MissingFact[] {
  const missing: MissingFact[] = [];
  if (!facts.birthYear) missing.push('birthYear');
  if (!facts.sex) missing.push('sex');
  if (!facts.heightCm) missing.push('heightCm');
  if (!facts.weightKg) missing.push('weightKg');
  return missing;
}

/**
 * Grundumsatz nach Mifflin-St Jeor (kcal/Tag), gerundet.
 *
 * Alter in vollen Jahren aus dem Geburtsjahr — auf ein Jahr genau reicht: Das
 * macht 5 kcal aus, weniger als jede Waage schwankt. `null`, wenn etwas fehlt.
 */
export function basalMetabolicRate(facts: BodyFacts, year: number): number | null {
  if (missingFacts(facts).length > 0) return null;
  const age = year - facts.birthYear!;
  const base = 10 * facts.weightKg! + 6.25 * facts.heightCm! - 5 * age;
  return Math.round(base + (facts.sex === 'male' ? 5 : -161));
}

/** Bedarf ohne Sport: Grundumsatz × Alltagsstufe. */
export function baseNeed(bmr: number, level: ActivityLevel): number {
  return Math.round(bmr * ACTIVITY_PAL[level]);
}

/**
 * Mehrverbrauch einer Aktivität über dem Grundumsatz: (MET − 1) × kg × Stunden.
 *
 * Minus eins, weil die Ruhe (1 MET) im Grundumsatz schon steckt — sonst zählte
 * die Stunde Training den Grundumsatz dieser Stunde ein zweites Mal.
 */
export function activityKcal(activity: Activity, minutes: number, weightKg: number): number {
  if (minutes <= 0 || weightKg <= 0) return 0;
  return Math.round((ACTIVITIES[activity] - 1) * weightKg * (minutes / 60));
}

export interface GoalSuggestion {
  kcal: number;
  proteinG: number;
  fatG: number;
  carbsG: number;
  /** Tagesauf- oder -abschlag gegenüber dem Bedarf (negativ = weniger essen). */
  deltaKcal: number;
}

/**
 * Zielvorschlag aus Bedarf, Gewicht, Ziel und Tempo.
 *
 * `need` ist der Bedarf eines typischen Tages **mit** Sport (der Wochenschnitt),
 * sonst wäre der Vorschlag an Trainingstagen zu knapp und an freien zu reich.
 */
export function suggestGoal(
  need: number,
  weightKg: number,
  goal: Goal,
  ratePct: number = DEFAULT_GOAL_RATE_PCT[goal],
): GoalSuggestion {
  const rate = goal === 'maintain' ? 0 : Math.min(Math.max(ratePct, 0), MAX_GOAL_RATE_PCT);
  const perDay = ((rate / 100) * weightKg * KCAL_PER_KG_BODY) / 7;
  const deltaKcal = Math.round(goal === 'lose' ? -perDay : perDay);
  const kcal = Math.round((need + deltaKcal) / 10) * 10;
  const proteinG = Math.round(weightKg * PROTEIN_G_PER_KG);
  const fatG = Math.round(weightKg * FAT_G_PER_KG);
  // Rest in Kohlenhydraten (4 kcal/g Protein und KH, 9 kcal/g Fett) — nie negativ.
  const carbsG = Math.max(0, Math.round((kcal - proteinG * 4 - fatG * 9) / 4));
  return { kcal, proteinG, fatG, carbsG, deltaKcal };
}

export type ExerciseState = 'planned' | 'done';

export interface DayExercise {
  activity: Activity;
  minutes: number;
  /** Geplant zählt als erwartet, bis es erledigt (oder abgesagt, dann fehlt es) ist. */
  state: ExerciseState;
}

export interface DayBalance {
  baseNeed: number;
  /** Mehrverbrauch aus erledigtem Sport. */
  doneKcal: number;
  /** Mehrverbrauch aus noch geplantem Sport — erwartet, nicht sicher. */
  plannedKcal: number;
  need: number;
  eaten: number;
  /** Gegessen minus Bedarf. Negativ = Defizit. */
  balance: number;
}

/** Tagesbilanz: gegessen gegen Bedarf, Sport getrennt nach erledigt und erwartet. */
export function dayBalance(
  base: number,
  exercises: readonly DayExercise[],
  weightKg: number,
  eaten: number,
): DayBalance {
  let doneKcal = 0;
  let plannedKcal = 0;
  for (const entry of exercises) {
    const kcal = activityKcal(entry.activity, entry.minutes, weightKg);
    if (entry.state === 'done') doneKcal += kcal;
    else plannedKcal += kcal;
  }
  const need = base + doneKcal + plannedKcal;
  return {
    baseNeed: base,
    doneKcal,
    plannedKcal,
    need,
    eaten: Math.round(eaten),
    balance: Math.round(eaten) - need,
  };
}

/**
 * Zählt die Aktivität eines Termins in **meiner** Bilanz? (E12/E16)
 *
 * Eigene private Termine: ja. Haushaltstermine: nur für den, der ihn angelegt
 * hat — und nur, wenn er selbst gemeint ist. „Fußball für Mia“, angelegt von
 * Mama, ist Mias Bewegung, nicht Mamas Kalorien.
 */
export function eventActivityCountsFor(input: {
  kind: 'event' | 'household';
  creatorId: string | null | undefined;
  me: string | null;
  forUserIds?: readonly string[] | null | undefined;
}): boolean {
  if (input.kind === 'event') return true;
  if (input.me === null || input.creatorId !== input.me) return false;
  const forWhom = input.forUserIds ?? [];
  return forWhom.length === 0 || forWhom.includes(input.me);
}
